import { LoaderCircle } from 'lucide-react';
import {
  ASSET_STATUS_LABEL,
  CONDITION_LABEL,
  formatDate,
  formatUtcDate,
  INTEGRATION_STATUS_LABEL,
  LOGIN_EVENT_LABEL,
  PERSON_TYPE_LABEL,
  SYNC_STATUS_LABEL,
} from '../lib/format';
import type {
  AssetCondition,
  AssetStatus,
  IntegrationStatus,
  LoginEventType,
  PersonStatus,
  PersonType,
  SyncStatus,
} from '../lib/types';
import { Badge } from './ui';

type Tone = Parameters<typeof Badge>[0]['tone'];

const ASSET_STATUS_TONE: Record<AssetStatus, Tone> = {
  AVAILABLE: 'green',
  CHECKED_OUT: 'blue',
  IN_REPAIR: 'amber',
  LOST: 'red',
  STOLEN: 'red',
  RETIRED: 'gray',
  DISPOSED: 'gray',
};

export function AssetStatusBadge({ status }: { status: AssetStatus }) {
  return <Badge tone={ASSET_STATUS_TONE[status]}>{ASSET_STATUS_LABEL[status]}</Badge>;
}

const CONDITION_TONE: Record<AssetCondition, Tone> = { NEW: 'brand', GOOD: 'green', FAIR: 'amber', POOR: 'amber', DAMAGED: 'red' };

export function ConditionBadge({ condition }: { condition: AssetCondition | null | undefined }) {
  if (!condition) return <span className="text-slate-400">—</span>;
  return <Badge tone={CONDITION_TONE[condition]}>{CONDITION_LABEL[condition]}</Badge>;
}

const PERSON_TYPE_TONE: Record<PersonType, Tone> = {
  STUDENT: 'brand',
  TEACHER: 'violet',
  STAFF: 'blue',
  ALUMNI: 'gray',
  PAST_STUDENT: 'gray',
  CANDIDATE: 'amber',
  OTHER: 'gray',
};

export function PersonTypeBadge({ type }: { type: PersonType }) {
  return <Badge tone={PERSON_TYPE_TONE[type]}>{PERSON_TYPE_LABEL[type]}</Badge>;
}

export function PersonStatusBadge({ status }: { status: PersonStatus }) {
  return status === 'ACTIVE' ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Inactive</Badge>;
}

const SYNC_TONE: Record<SyncStatus, Tone> = { QUEUED: 'gray', RUNNING: 'blue', SUCCEEDED: 'green', FAILED: 'red' };

export function SyncStatusBadge({ status }: { status: SyncStatus }) {
  return (
    <Badge tone={SYNC_TONE[status]}>
      {status === 'RUNNING' && <LoaderCircle className="size-3 animate-spin" />}
      {SYNC_STATUS_LABEL[status]}
    </Badge>
  );
}

const INTEGRATION_TONE: Record<IntegrationStatus, Tone> = { NOT_CONFIGURED: 'gray', CONNECTED: 'green', ERROR: 'red' };

export function IntegrationStatusBadge({ status, enabled = true }: { status: IntegrationStatus; enabled?: boolean }) {
  if (!enabled) return <Badge tone="gray">Off</Badge>;
  return <Badge tone={INTEGRATION_TONE[status]}>{INTEGRATION_STATUS_LABEL[status]}</Badge>;
}

export function DeviceStatusBadge({ status, removed }: { status: string | null; removed?: boolean }) {
  if (removed) return <Badge tone="gray">Removed from Google</Badge>;
  if (!status) return <span className="text-slate-400">—</span>;
  const tone: Tone = status === 'ACTIVE' ? 'green' : status === 'DEPROVISIONED' || status === 'INACTIVE' ? 'gray' : status === 'DISABLED' ? 'red' : 'amber';
  const label = status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
  return <Badge tone={tone}>{label}</Badge>;
}

const LOGIN_TONE: Record<LoginEventType, Tone> = { LOGIN: 'green', LOGOUT: 'gray', LOGIN_FAILURE: 'red', OTHER: 'gray' };

export function LoginEventBadge({ type }: { type: LoginEventType }) {
  return <Badge tone={LOGIN_TONE[type]}>{LOGIN_EVENT_LABEL[type]}</Badge>;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export function DueBadge({ dueAt, checkedInAt }: { dueAt: string | null; checkedInAt?: string | null }) {
  if (!dueAt) return <span className="text-slate-400">No due date</span>;
  if (checkedInAt) return <span className="text-slate-600">{formatDate(dueAt)}</span>;
  const due = new Date(dueAt);
  // Calendar days, so something due at 3:30 this afternoon is due today, and something due last night is overdue.
  const days = Math.round((startOfDay(due) - startOfDay(new Date())) / 86_400_000);
  if (due.getTime() < Date.now()) {
    return <Badge tone="red">{days === 0 ? 'Overdue' : `Overdue ${-days} day${days === -1 ? '' : 's'}`}</Badge>;
  }
  if (days <= 3) return <Badge tone="amber">Due {days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`}</Badge>;
  return <span className="text-slate-600">{formatDate(dueAt)}</span>;
}

export function AueBadge({ date }: { date: string | null }) {
  if (!date) return <span className="text-slate-400">—</span>;
  const ms = new Date(date).getTime() - Date.now();
  // Google reports auto-update expiration as a calendar date at UTC midnight.
  const label = formatUtcDate(date);
  if (ms < 0) return <Badge tone="red">Expired {label}</Badge>;
  if (ms < 365 * 86_400_000) return <Badge tone="amber">{label}</Badge>;
  return <span className="text-slate-600">{label}</span>;
}
